import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const LibraryCatalog = ({ onNavigate, userInfo }) => {
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [categories, setCategories] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Add Book Modal
    const [showAddModal, setShowAddModal] = useState(false);
    const [addForm, setAddForm] = useState({
        accession_number: '', isbn: '', title: '', author: '',
        publisher: '', publication_year: '', category: '', total_copies: 1, price: '', coverImage: null
    });
    const [addLoading, setAddLoading] = useState(false);
    const [addError, setAddError] = useState('');

    // Edit Book Modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editBook, setEditBook] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState('');

    const token = sessionStorage.getItem('token') || localStorage.getItem('token');

    // Fetch categories for dropdowns
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await fetch(`${API_LINK}/api/library/categories`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) setCategories(data.data.categories);
            } catch { /* silent */ }
        };
        fetchCategories();
    }, [token]);

    const fetchBooks = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                page,
                limit: 15,
                ...(searchTerm && { search: searchTerm }),
                ...(categoryFilter && categoryFilter !== 'All' && { category: categoryFilter })
            });
            const res = await fetch(`${API_LINK}/api/library/books?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setBooks(data.data.books);
                setTotal(data.data.total);
                setTotalPages(data.data.totalPages);

                // Build dynamic category list from fetched data
                setCategories(prev => {
                    const cats = new Set(['All']);
                    data.data.books.forEach(b => { if (b.category) cats.add(b.category); });
                    prev.forEach(c => cats.add(c));
                    return Array.from(cats);
                });
            } else {
                setError(data.message || 'Failed to fetch books');
            }
        } catch (err) {
            setError('Could not connect to server. Please check your connection.');
        } finally {
            setLoading(false);
        }
    }, [token, page, searchTerm, categoryFilter]);

    useEffect(() => {
        const timer = setTimeout(fetchBooks, 400); // debounce search
        return () => clearTimeout(timer);
    }, [fetchBooks]);

    const handleAddBook = async (e) => {
        e.preventDefault();
        setAddLoading(true);
        setAddError('');
        try {
            const formData = new FormData();
            Object.keys(addForm).forEach(key => {
                if (key === 'coverImage' && addForm[key]) {
                    formData.append('coverImage', addForm[key]);
                } else if (key !== 'coverImage') {
                    formData.append(key, addForm[key]);
                }
            });

            const res = await fetch(`${API_LINK}/api/library/books`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }, // Content-Type omitted for FormData
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                setShowAddModal(false);
                setAddForm({ accession_number: '', isbn: '', title: '', author: '', publisher: '', publication_year: '', category: '', total_copies: 1, price: '', coverImage: null });
                fetchBooks();
            } else {
                setAddError(data.message || 'Failed to add book');
            }
        } catch {
            setAddError('Could not connect to server');
        } finally {
            setAddLoading(false);
        }
    };

    const openEditModal = (book) => {
        setEditBook({ ...book, coverImage: null });
        setEditError('');
        setShowEditModal(true);
    };

    const handleEditBook = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        setEditError('');
        try {
            const formData = new FormData();

            // Append basic fields
            const fields = ['title', 'author', 'isbn', 'publisher', 'publication_year', 'category', 'total_copies', 'available_copies', 'price', 'location', 'shelf_number'];
            fields.forEach(field => {
                if (editBook[field] !== undefined && editBook[field] !== null) {
                    formData.append(field, editBook[field]);
                }
            });

            // Append image file if selected
            if (editBook.coverImage) {
                formData.append('coverImage', editBook.coverImage);
            }

            const res = await fetch(`${API_LINK}/api/library/books/${editBook.id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }, // Content-Type omitted for FormData
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                setShowEditModal(false);
                setEditBook(null);
                fetchBooks();
            } else {
                setEditError(data.message || 'Failed to update book');
            }
        } catch {
            setEditError('Could not connect to server');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteBook = async (id, title) => {
        if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
        try {
            const res = await fetch(`${API_LINK}/api/library/books/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) fetchBooks();
            else alert(data.message || 'Failed to delete book');
        } catch {
            alert('Could not connect to server');
        }
    };

    const inputStyle = {
        width: '100%', padding: '10px 12px', border: '1px solid #ddd',
        borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginTop: '4px'
    };

    return (
        <Layout role="librarian" onNavigate={onNavigate} activePage="library-catalog" pageTitle="Book Catalog">
            <div style={{ padding: '20px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <h2 style={{ color: '#333', margin: 0 }}>📚 Book Catalog</h2>
                        <p style={{ color: '#666', margin: '4px 0 0', fontSize: '14px' }}>{total} books in library</p>
                    </div>
                    <button
                        onClick={() => setShowAddModal(true)}
                        style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                    >
                        + Add New Book
                    </button>
                </div>

                {/* Filters */}
                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '250px' }}>
                        <input
                            type="text"
                            placeholder="Search by Title, Author, or ISBN..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                            style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                    </div>
                    <div>
                        <select
                            value={categoryFilter}
                            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                            style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', minWidth: '150px' }}
                        >
                            <option value="">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '1rem', color: '#dc2626', fontSize: '14px' }}>
                        ⚠️ {error}
                    </div>
                )}

                {/* Books Table */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#238548', color: 'white' }}>
                                    <th style={{ padding: '15px', textAlign: 'left' }}>Title</th>
                                    <th style={{ padding: '15px', textAlign: 'left' }}>Author</th>
                                    <th style={{ padding: '15px', textAlign: 'left' }}>Category</th>
                                    <th style={{ padding: '15px', textAlign: 'left' }}>ISBN</th>
                                    <th style={{ padding: '15px', textAlign: 'center' }}>Status</th>
                                    <th style={{ padding: '15px', textAlign: 'center' }}>Available</th>
                                    <th style={{ padding: '15px', textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>⏳ Loading books...</td></tr>
                                ) : books.length > 0 ? (
                                    books.map((book, index) => (
                                        <tr key={book.id} style={{ borderBottom: '1px solid #eee', backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                            <td style={{ padding: '15px' }}><strong>{book.title}</strong></td>
                                            <td style={{ padding: '15px', color: '#555' }}>{book.author}</td>
                                            <td style={{ padding: '15px' }}>
                                                {book.category && (
                                                    <span style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                                                        {book.category}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '15px', fontFamily: 'monospace', fontSize: '13px' }}>{book.isbn || '—'}</td>
                                            <td style={{ padding: '15px', textAlign: 'center' }}>
                                                <span style={{ color: book.available_copies > 0 ? '#2e7d32' : '#c62828', fontWeight: '600' }}>
                                                    {book.available_copies > 0 ? '✅ Available' : '❌ Out of Stock'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '15px', textAlign: 'center', fontWeight: '600' }}>
                                                {book.available_copies} / {book.total_copies}
                                            </td>
                                            <td style={{ padding: '15px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                    <button
                                                        onClick={() => openEditModal(book)}
                                                        style={{ backgroundColor: 'transparent', border: '1px solid #1976d2', color: '#1976d2', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteBook(book.id, book.title)}
                                                        style={{ backgroundColor: 'transparent', border: '1px solid #dc2626', color: '#dc2626', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                                    >
                                                        🗑 Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>No books found. Add some books using the button above.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '16px', borderTop: '1px solid #eee' }}>
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #ddd', cursor: page === 1 ? 'not-allowed' : 'pointer', backgroundColor: page === 1 ? '#f5f5f5' : 'white' }}>
                                ← Prev
                            </button>
                            <span style={{ padding: '6px 14px', color: '#555', fontSize: '14px' }}>Page {page} of {totalPages}</span>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #ddd', cursor: page === totalPages ? 'not-allowed' : 'pointer', backgroundColor: page === totalPages ? '#f5f5f5' : 'white' }}>
                                Next →
                            </button>
                        </div>
                    )}
                </div>

                {/* Edit Book Modal */}
                {showEditModal && editBook && (
                    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                        <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h3 style={{ margin: 0, color: '#1976d2' }}>✏️ Edit Book</h3>
                                <button onClick={() => setShowEditModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>✕</button>
                            </div>
                            {editError && <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px', marginBottom: '1rem', color: '#dc2626', fontSize: '13px' }}>⚠️ {editError}</div>}
                            <form onSubmit={handleEditBook}>
                                {[
                                    { label: 'Book Title *', key: 'title', required: true },
                                    { label: 'Author *', key: 'author', required: true },
                                    { label: 'ISBN', key: 'isbn' },
                                    { label: 'Publisher', key: 'publisher' },
                                    { label: 'Publication Year', key: 'publication_year', type: 'number' },
                                    { label: 'Total Copies', key: 'total_copies', type: 'number' },
                                    { label: 'Available Copies', key: 'available_copies', type: 'number' },
                                    { label: 'Price (₹)', key: 'price', type: 'number' },
                                    { label: 'Location / Shelf No.', key: 'shelf_number' },
                                ].map(({ label, key, required, type }) => (
                                    <div key={key} style={{ marginBottom: '1rem' }}>
                                        <label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>{label}</label>
                                        <input
                                            type={type || 'text'}
                                            required={required}
                                            value={editBook[key] || ''}
                                            onChange={e => setEditBook(prev => ({ ...prev, [key]: e.target.value }))}
                                            style={inputStyle}
                                        />
                                    </div>
                                ))}
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Category</label>
                                    <select
                                        value={editBook.category || ''}
                                        onChange={e => setEditBook(prev => ({ ...prev, category: e.target.value }))}
                                        style={inputStyle}
                                    >
                                        <option value="">-- Select Category --</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Cover Image</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={e => setEditBook(prev => ({ ...prev, coverImage: e.target.files[0] }))}
                                        style={inputStyle}
                                    />
                                    <small style={{ color: '#888' }}>Upload a new image to replace the existing one.</small>
                                </div>
                                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                                    <button type="submit" disabled={editLoading}
                                        style={{ flex: 1, backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                                        {editLoading ? 'Saving...' : '💾 Save Changes'}
                                    </button>
                                    <button type="button" onClick={() => setShowEditModal(false)}
                                        style={{ flex: 1, backgroundColor: '#f5f5f5', color: '#555', border: '1px solid #ddd', padding: '12px', borderRadius: '8px', cursor: 'pointer' }}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Add Book Modal */}
                {showAddModal && (
                    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                        <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h3 style={{ margin: 0, color: '#238548' }}>📖 Add New Book</h3>
                                <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>✕</button>
                            </div>
                            {addError && <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px', marginBottom: '1rem', color: '#dc2626', fontSize: '13px' }}>⚠️ {addError}</div>}
                            <form onSubmit={handleAddBook}>
                                {[
                                    { label: 'Accession Number *', key: 'accession_number', required: true },
                                    { label: 'Book Title *', key: 'title', required: true },
                                    { label: 'Author *', key: 'author', required: true },
                                    { label: 'ISBN', key: 'isbn' },
                                    { label: 'Publisher', key: 'publisher' },
                                    { label: 'Publication Year', key: 'publication_year', type: 'number' },
                                    { label: 'Total Copies', key: 'total_copies', type: 'number' },
                                    { label: 'Price (₹)', key: 'price', type: 'number' },
                                ].map(({ label, key, required, type }) => (
                                    <div key={key} style={{ marginBottom: '1rem' }}>
                                        <label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>{label}</label>
                                        <input
                                            type={type || 'text'}
                                            required={required}
                                            value={addForm[key]}
                                            onChange={e => setAddForm(prev => ({ ...prev, [key]: e.target.value }))}
                                            style={inputStyle}
                                        />
                                    </div>
                                ))}
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Category</label>
                                    <select
                                        value={addForm.category}
                                        onChange={e => setAddForm(prev => ({ ...prev, category: e.target.value }))}
                                        style={inputStyle}
                                    >
                                        <option value="">-- Select Category --</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Cover Image</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={e => setAddForm(prev => ({ ...prev, coverImage: e.target.files[0] }))}
                                        style={inputStyle}
                                    />
                                    <small style={{ color: '#888' }}>Upload an image for the book cover (optional).</small>
                                </div>
                                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                                    <button type="submit" disabled={addLoading}
                                        style={{ flex: 1, backgroundColor: '#238548', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                                        {addLoading ? 'Adding...' : '+ Add Book'}
                                    </button>
                                    <button type="button" onClick={() => setShowAddModal(false)}
                                        style={{ flex: 1, backgroundColor: '#f5f5f5', color: '#555', border: '1px solid #ddd', padding: '12px', borderRadius: '8px', cursor: 'pointer' }}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default LibraryCatalog;
